import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import ItemService from '../../../Services/QRCodeServices/MenuItem/ItemService';

export const MenuItemDropdown = ({ value, onChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 100;

  // Fetch initial items on component mount
  useEffect(() => {
    const fetchItems = async () => {
      try {
        setLoading(true);
        const response = await ItemService.getList({
          pageNumber: 1,
          pageSize,
        });

        // Transform data into the format required by react-select
        const options = response.data.map((item) => ({
          value: item.id,
          label: item.title,
        }));

        setItems(options);
        setHasMore(response.totalData > pageSize);
        setError(null);
      } catch (err) {
        setError('Failed to fetch menu items');
        console.error('Error fetching items:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, []);

  // Load more items when scrolling
  const loadMoreItems = async () => {
    if (loading || !hasMore) return;

    try {
      setLoading(true);
      const nextPage = pageNumber + 1;
      const response = await ItemService.getList({
        pageNumber: nextPage,
        pageSize,
      });

      // Transform new data into the format required by react-select
      const newItems = response.data.map((item) => ({
        value: item.id,
        label: item.title,
      }));

      setItems((prevItems) => [...prevItems, ...newItems]);
      setPageNumber(nextPage);
      setHasMore(response.totalData > nextPage * pageSize);
    } catch (err) {
      console.error('Error loading more items:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle scrolling to load more items
  const handleMenuScrollToBottom = () => {
    if (!loading && hasMore) {
      loadMoreItems();
    }
  };

  // Handle change event
  const handleChange = (selectedOptions) => {
    // Pass the selected values back to the parent component
    onChange(selectedOptions || []); // Ensure selectedOptions is always an array
  };

  if (loading && items.length === 0) {
    return <Select isDisabled placeholder="Loading menu items..." />;
  }

  if (error && items.length === 0) {
    return <Select isDisabled placeholder="Error loading menu items" />;
  }

  return (
    <Select
      isMulti // Enable multi-select
      isClearable // Enable clearable options
      isSearchable // Enable searchable filtering
      options={items} // Pass the items as options
      value={value} // Controlled value
      onChange={handleChange} // Handle change event
      onMenuScrollToBottom={handleMenuScrollToBottom} // Load more items on scroll
      placeholder="Select Menu Item"
      isLoading={loading} // Show loading indicator
      noOptionsMessage={() => (loading ? 'Loading more...' : 'No items available')}
    />
  );
};